// The dashboard imports values from this file directly (program-summary.ts), so it must
// stay free of runtime imports: a value import here would pull server code, or a
// database client, into the dashboard bundle. Types only.
import type { ExperimentSignals, SignificanceFigures } from '../attention/attention-items';

/**
 * What a concluded experiment taught the programme, in one word.
 *
 * Not pickWinner. That function answers "may an unattended deploy roll something out",
 * and returns null for anything a person should look at first: two winners, or a
 * winner beside a loser. Here the question is retrospective and nothing acts on the
 * answer, so those cases get words of their own instead of collapsing into "nothing".
 *
 *   won           at least one arm is confidently better and none is confidently worse.
 *                 Two winners is still a win for the programme; which one to ship is a
 *                 separate decision that pickWinner rightly leaves to a person.
 *   lost          at least one arm is confidently worse and none is better
 *   mixed         confidently better and confidently worse arms in the same test
 *   inconclusive  there is a reading, and nothing in it is confidently different
 *   no-data       no stored statistics, so there is nothing to read
 *
 * "Confidently" is the interval, not the significance flag, for the reason pickWinner
 * gives: a variant can be flagged significant with an interval straddling zero once
 * the corrected p-value and SPRT disagree, and that is not a direction.
 */
export const PROGRAM_OUTCOMES = ['won', 'lost', 'mixed', 'inconclusive', 'no-data'] as const;
export type ProgramOutcome = (typeof PROGRAM_OUTCOMES)[number];

export type OutcomeReading = {
  /** The programme's outcome: the statistics, with a deployment counted as a win. */
  outcome: ProgramOutcome;
  /**
   * What the statistics alone said. Kept beside `outcome` because some figures must
   * not count a deployment as a win: "winners shipped" would be circular (every
   * deployed test is a shipped winner by definition), and "does prioritisation predict
   * wins" would be biased by which tests people choose to deploy.
   */
  statisticalOutcome: ProgramOutcome;
  /**
   * The headline arm: the best winner, or for a loss the worst loser. Null when the
   * outcome has no direction to headline.
   */
  variantName: string | null;
  /** That arm's point-estimate improvement over control, in percent. */
  lift: number | null;
  /** That arm's lift interval, as fractions (0.05 is +5%). */
  liftLower: number | null;
  liftUpper: number | null;
  /** Won because a variant was deployed, rather than on the statistics alone. */
  byDeployment: boolean;
};

type Arm = { variantId: string; figures: SignificanceFigures; lift: number | null };

const NO_DIRECTION = { variantName: null, lift: null, liftLower: null, liftUpper: null, byDeployment: false };

type Headline = Omit<OutcomeReading, 'outcome' | 'statisticalOutcome'>;

/** A reading straight from the statistics, before any deployment is applied. */
const statistical = (outcome: ProgramOutcome, head: Headline): OutcomeReading => ({
  outcome,
  statisticalOutcome: outcome,
  ...head,
});

const headline = (arm: Arm): Headline => ({
  variantName: arm.figures.variantName,
  lift: arm.lift,
  liftLower: arm.figures.liftLower,
  liftUpper: arm.figures.liftUpper,
  byDeployment: false,
});

/**
 * The point estimate when there is one, else the interval's midpoint in percent, so
 * an arm with a stored interval but no improvement figure still sorts sensibly.
 */
const sortKey = (arm: Arm): number => {
  if (arm.lift !== null) return arm.lift;
  const { liftLower, liftUpper } = arm.figures;
  return liftLower !== null && liftUpper !== null ? ((liftLower + liftUpper) / 2) * 100 : 0;
};

export function classifyOutcome(signals: ExperimentSignals | null): OutcomeReading {
  const entries = signals ? Object.entries(signals.significance) : [];
  if (!signals || !entries.length) return statistical('no-data', NO_DIRECTION);

  const winners: Arm[] = [];
  const losers: Arm[] = [];

  for (const [variantId, figures] of entries) {
    if (!figures.significant) continue;

    const { liftLower, liftUpper } = figures;
    if (liftLower === null || liftUpper === null) continue;

    const arm = { variantId, figures, lift: signals.variants[variantId]?.improvement ?? null };
    if (liftLower > 0) winners.push(arm);
    else if (liftUpper < 0) losers.push(arm);
  }

  const best = [...winners].sort((a, b) => sortKey(b) - sortKey(a))[0];
  const worst = [...losers].sort((a, b) => sortKey(a) - sortKey(b))[0];

  if (best && worst) return statistical('mixed', headline(best));
  if (best) return statistical('won', headline(best));
  if (worst) return statistical('lost', headline(worst));
  return statistical('inconclusive', NO_DIRECTION);
}

/**
 * A deployed test counts as won, whatever the statistics said.
 *
 * Deploying is a person deciding the variant is better and rolling it out to everyone,
 * and the programme reports that decision. "Deployed" means ever deployed, not the
 * current status: a winner rolled out and later ended still shipped.
 *
 * The headline is kept only where the statistics named a winner (won, or the winner
 * side of mixed). For an inconclusive or losing reading there is no winning arm whose
 * lift could honestly be shown next to "won", so the lift is left empty.
 *
 * No data stays no data: with no statistics there is no evidence either way, and the
 * screen leaves such tests out entirely, deployed or not. `statisticalOutcome` is never
 * changed here.
 */
export function withDeployment(reading: OutcomeReading, deployed: boolean): OutcomeReading {
  if (!deployed || reading.outcome === 'won' || reading.outcome === 'no-data') return reading;
  if (reading.outcome === 'mixed') return { ...reading, outcome: 'won', byDeployment: true };
  return { ...reading, ...NO_DIRECTION, outcome: 'won', byDeployment: true };
}

/**
 * The outcomes that count as decided. Applied to the programme outcome for the headline
 * win rate (so a deployed inconclusive test is in it, as a win), and to the
 * statistical outcome for the breakdowns, which only a significant result enters.
 */
export const SIGNIFICANT_OUTCOMES: readonly ProgramOutcome[] = ['won', 'lost', 'mixed'];
